import { Checkbox, MenuItem, Select, SvgIcon, TableCell, TableRow } from '@mui/material';
import InviteIcon from 'assets/icons/invite.svg';
import TooltipIfFromTeam from 'components/@home/drawers/ChannelDrawer/Form/TooltipIfFromTeam';
import LineClampTooltip from 'components/common/LineClampTooltip';
import isEmpty from 'lodash/isEmpty';
import { bool, func, object, string } from 'prop-types';
import React, { useCallback } from 'react';
import { I18n } from 'utils/i18n';
import employeeShape from 'shapes/employee';
import matchStringArray from 'utils/matchStringArray';

const getName = e =>
  [e.firstName, e.surName].filter(v => v).join(' ') + (isEmpty(e.code) ? '' : ` - ${e.code}`);

const cellSx = {
  minWidth: '110px',
};

const inactiveCellSx = {
  minWidth: '110px',
  color: 'secondary.main',
};

const EmployeeRow = ({ employee, onSelect, onSetRole, member, searchString = '', disabled }) => {
  const isInactive = !employee.isAcceptTerms;

  const select = useCallback(() => {
    if (!member?.fromTeams?.length) {
      onSelect(employee);
    }
  }, [member, onSelect, employee]);

  const onChangeRole = useCallback(
    e => {
      onSetRole(employee, e.target.value);
    },
    [onSetRole, employee],
  );

  const cellStyle = isInactive ? inactiveCellSx : cellSx;

  return (
    <TableRow
      hover
      onClick={select}
      selected={!!member}
      sx={[
        theme => ({
          border: 0,
          cursor: 'pointer',
          '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.secondary.ultraUltraLight,
          },
        }),
        disabled
          ? {
              pointerEvents: 'none',
            }
          : {
              pointerEvents: 'auto',
            },
        disabled
          ? {
              opacity: 0.5,
            }
          : {
              opacity: 1,
            },
      ]}
    >
      <TableCell padding="checkbox" sx={{ position: 'relative', padding: 0, width: 'auto' }}>
        {isInactive && (
          <SvgIcon
            sx={{
              width: '16px',
              top: '12px',
              position: 'absolute',
              left: '42px',
              color: 'secondary.main',
            }}
          >
            <InviteIcon />
          </SvgIcon>
        )}
        <TooltipIfFromTeam member={member} teams={[]}>
          <Checkbox color="primary" checked={!!member} disabled={member?.fromTeams?.length} />
        </TooltipIfFromTeam>
      </TableCell>
      <TableCell sx={cellStyle} padding="normal">
        <LineClampTooltip>{getName(employee)}</LineClampTooltip>
      </TableCell>
      <TableCell sx={cellStyle}>
        <LineClampTooltip>{employee.department}</LineClampTooltip>
      </TableCell>
      <TableCell sx={cellStyle}>
        <LineClampTooltip>{employee.jobTitle}</LineClampTooltip>
      </TableCell>
      <TableCell sx={cellStyle}>
        <LineClampTooltip>{employee.location}</LineClampTooltip>
      </TableCell>
      <TableCell sx={cellStyle}>
        {!!member && (
          <Select
            MenuProps={{ onClick: e => e.stopPropagation() }}
            style={{ width: 100 }}
            value={member?.groupRole}
            onChange={onChangeRole}
          >
            {['member', 'writer', 'admin'].map(r => (
              <MenuItem key={r} value={r}>
                {I18n.t(`Roles.${r}`)}
              </MenuItem>
            ))}
          </Select>
        )}
      </TableCell>
      <TableCell sx={cellStyle}>
        <LineClampTooltip>
          {matchStringArray(employee.tags, searchString).join(', ')}
        </LineClampTooltip>
      </TableCell>
    </TableRow>
  );
};

EmployeeRow.propTypes = {
  employee: employeeShape.isRequired,
  member: object.isRequired,
  onSelect: func.isRequired,
  searchString: string,
  onSetRole: func.isRequired,
  disabled: bool.isRequired,
};

export default EmployeeRow;
